import React, {
   CSSProperties,
   ReactNode,
   Ref,
   useCallback,
} from 'react';
import classnames from 'classnames';
import {PaddingType, Panel } from '../panel';
import {VerticalSplitPanel} from '../verticalsplitpanel';
import {HorizontalSplitPanel} from '../horizontalsplitpanel';
import {VerticalPanel} from '../verticalpanel';
import {HorizontalPanel} from '../horizontalpanel';

/**
 * {@docCategory ResizableBorderPanel}
 */
export type DirectionType = 'vertical' | 'horizontal';

/**
 * {@docCategory ResizableBorderPanel}
 */
export type ResizableRegion = 'top' | 'bottom' | 'left' | 'right';

/**
 * {@docCategory ResizableBorderPanel}
 */
export interface ResizableBorderPanelProps {
   /**
    * The top component.
    */
   top?: ReactNode;

   /**
    * The bottom component.
    */
   bottom?: ReactNode;

   /**
    * The left component.
    */
   left?: ReactNode;

   /**
    * The right component.
    */
   right?: ReactNode;

   /**
    * Specify the resizable regions
    */
   resizableRegions?: ResizableRegion[] | 'all';

   /**
    * Array of (relative) default sizes for the individual panes. Missing values default to 1. When
    * no defaultSizes are passed, all sizes default to 1, equally distributing the available space
    * (as far as minSize values permit).  This array should contain 1 value if only the left or right
    * region is resizable.  If both regions are resizable, the array should contain 2 values.  If the
    * value is a string and ends with px, it is treated as a pixel value rather than a flex value.  A
    * value of 'layout' specifies to use the layout size as the default size.
    */
   defaultHorizontalSizes?: (string | number)[];

   /**
    * Array of default sizes for the individual panes. Missing values default to 1. When
    * no defaultSizes are passed, all sizes default to 1, equally distributing the available space
    * (as far as minSize values permit).  This array should contain 1 value if only the top or bottom
    * region is resizable.  If both regions are resizable, the array should contain 2 values.  If the
    * value is a string and ends with px, it is treated as a pixel value rather than a flex value.  A
    * value of 'layout' specifies to use the layout size as the default size.
    */
   defaultVerticalSizes?: (string | number)[];

   /**
    * Minimum size of all panes (in pixels), or array containing individual minimum sizes for each
    * pane.  This array should contain 1 value if only the left or right region is resizable.  If
    * both regions are resizable, the array should contain 2 values.  If not specified, the default
    * value is 0.
    */
   minHorizontalSizes?: number[];

   /**
    * Minimum size of all panes (in pixels), or array containing individual minimum sizes for each
    * pane.  This array should contain 1 value if only the top or bottom region is resizable.  If
    * both regions are resizable, the array should contain 2 values.  If not specified, the default
    * value is 0.
    */
   minVerticalSizes?: number[];

   /**
    * Maximum size of all panes (in pixels), or array containing individual maximum sizes for each
    * pane.  This array should contain 1 value if only the left or right region is resizable.  If
    * both regions are resizable, the array should contain 2 values.  If not specified, the default
    * value is -1 (no maximum).
    */
   maxHorizontalSizes?: number[];

   /**
    * Maximum size of all panes (in pixels), or array containing individual maximum sizes for each
    * pane.  This array should contain 1 value if only the top or bottom region is resizable.  If
    * both regions are resizable, the array should contain 2 values.  If not specified, the default
    * value is -1 (no maximum).
    */
   maxVerticalSizes?: number[];

   /**
    * Used to temporarily disable resizing on a resizer index.  If there are two regions, there will
    * be only one resizer index, which starts at 0.
    */
   disableHorizontalResizers?: number | number[];

   /**
    * Used to temporarily disable resizing on a resizer index.  If there are two regions, there will
    * be only one resizer index, which starts at 0.
    */
   disableVerticalResizers?: number | number[];

   /**
    * Determines if the component has a border around it.
    *
    * @default false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * The width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * The height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The minimum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<any>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * Called when the panel is clicked.
    */
   onClick?: (event: React.MouseEvent<HTMLElement>) => void;

   /**
    * This callback is invoked when a drag starts.
    *
    * @param direction - The resize direction
    */
   onDragStarted?: (direction: DirectionType) => void;

   /**
    * This callback is invoked when a drag ends.
    *
    * @param direction - The resize direction
    * @param sizes - The sizes of the various regions in the resize direction
    */
   onDragFinished?: (direction: DirectionType, sizes: number[]) => void;

   /**
    * This callback is invoked with the current drag during a drag event. It is recommended that it
    * is wrapped in a debounce function.
    *
    * @param direction - The resize direction
    * @param sizes - The sizes of the various regions in the resize direction
    */
   onChange?: (direction: DirectionType, sizes: number[]) => void;
}

export const ResizableBorderPanel: React.FC<React.PropsWithChildren<ResizableBorderPanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: React.PropsWithChildren<ResizableBorderPanelProps>,
    ref: React.Ref<HTMLDivElement>) => {

      const { onDragStarted, onDragFinished, onChange } = props;

      const onDragStartedHandler = useCallback((direction: 'horizontal' | 'vertical') => (() => {
         if (onDragStarted) {
            onDragStarted(direction);
         }
      }),[onDragStarted]);

      const onDragFinishedHandler = useCallback((direction: 'horizontal' | 'vertical') => ((sizes: number[]) => {
         if (onDragFinished) {
            onDragFinished(direction, sizes);
         }
      }),[onDragFinished]);

      const onChangeHandler = useCallback((direction: 'horizontal' | 'vertical') => ((sizes: number[]) => {
         if (onChange) {
            onChange(direction, sizes);
         }
      }),[onChange]);

      const defaultHorizontalSizes = getDefaultHorizontalSizes(props.resizableRegions, props.defaultHorizontalSizes);
      const defaultVerticalSizes = getDefaultVerticalSizes(props.resizableRegions, props.defaultVerticalSizes);

      const minHorizontalSizes = getMinHorizontalSizes(props.resizableRegions, props.minHorizontalSizes);
      const minVerticalSizes = getMinVerticalSizes(props.resizableRegions, props.minVerticalSizes);

      const maxHorizontalSizes = getMaxHorizontalSizes(props.resizableRegions, props.maxHorizontalSizes);
      const maxVerticalSizes = getMaxVerticalSizes(props.resizableRegions, props.maxVerticalSizes);

      const disableHorizontalResizers = getDisabledHorizontalResizers(props.resizableRegions, props.disableHorizontalResizers);
      const disableVerticalResizers = getDisabledVerticalResizers(props.resizableRegions, props.disableVerticalResizers);

      return (
         <Panel
            className={classnames('uf-resizable-border-panel', props.className)}
            style={props.style}
            border={props.border}
            padding={props.padding}
            paddingLeft={props.paddingLeft}
            paddingRight={props.paddingRight}
            paddingTop={props.paddingTop}
            paddingBottom={props.paddingBottom}
            fill={true}
            width={props.width}
            height={props.height}
            minWidth={props.minWidth}
            minHeight={props.minHeight}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            ref={ref}
            id={props.id}
            onClick={props.onClick}
         >
            <div className="uf-resizable-border-panel-center">
               <VerticalSplitPanel
                  minSize={minVerticalSizes}
                  maxSize={maxVerticalSizes}
                  defaultSizes={defaultVerticalSizes}
                  disableResizers={disableVerticalResizers}
                  onDragStarted={onDragStartedHandler('vertical')}
                  onDragFinished={onDragFinishedHandler('vertical')}
                  onChange={onChangeHandler('vertical')}
               >
                  <VerticalPanel className="uf-resizable-border-panel-resizable-top" align={'top'}>
                     {props.top}
                  </VerticalPanel>
                  <Panel className={"uf-resizable-border-panel"} fill={true}>
                     <div className={'uf-resizable-border-panel-center'}>
                        <HorizontalSplitPanel
                           minSize={minHorizontalSizes}
                           maxSize={maxHorizontalSizes}
                           defaultSizes={defaultHorizontalSizes}
                           disableResizers={disableHorizontalResizers}
                           onDragStarted={onDragStartedHandler('horizontal')}
                           onDragFinished={onDragFinishedHandler('horizontal')}
                           onChange={onChangeHandler('horizontal')}
                        >
                           <HorizontalPanel className="uf-resizable-border-panel-resizable-left" align={'left'}>
                              {props.left}
                           </HorizontalPanel>
                           <Panel className="uf-resizable-border-panel-resizable-center" fill={true}>
                              {props.children}
                           </Panel>
                           <HorizontalPanel className="uf-resizable-border-panel-resizable-right" align={'right'}>
                              {props.right}
                           </HorizontalPanel>
                        </HorizontalSplitPanel>
                     </div>
                  </Panel>
                  <VerticalPanel className="uf-resizable-border-panel-resizable-bottom" align={'bottom'}>
                     {props.bottom}
                  </VerticalPanel>
               </VerticalSplitPanel>
            </div>
         </Panel>
      );
});

const getSizeArray = <T,>(resizableRegions: ResizableRegion[] | 'all' | undefined, defaultSizes: T[] | undefined, region1: ResizableRegion, region2: ResizableRegion, defaultSize: T, stretchSize: T):T[] => {

   if (!resizableRegions) {
      // Neither region is resizable
      return [defaultSize,stretchSize,defaultSize];
   }

   if (resizableRegions !== 'all') {
      if (resizableRegions.indexOf(region2) < 0) {
         if (resizableRegions.indexOf(region1) < 0) {
            // Neither region is resizable
            return [defaultSize,stretchSize,defaultSize];
         }
         else {
            // Only region1 is resizable
            if (!defaultSizes || defaultSizes.length<1) {
               return [defaultSize,stretchSize,defaultSize];
            }
            return [defaultSizes[0],stretchSize,defaultSize];
         }
      }
      else {
         if (resizableRegions.indexOf(region1) < 0) {
            // Only region2 is resizable
            if (!defaultSizes || defaultSizes.length<1) {
               return [defaultSize,stretchSize,defaultSize];
            }
            return [defaultSize,stretchSize,defaultSizes[0]];
         }
      }
   }

   // Both regions are resizable
   if (!defaultSizes || defaultSizes.length<1) {
      return [defaultSize,stretchSize,defaultSize];
   }
   if (defaultSizes.length < 2) {
      return [defaultSizes[0],stretchSize,defaultSize];
   }
   return [defaultSizes[0],stretchSize,defaultSizes[1]];
};

const getDisabledResizers = (resizableRegions: ResizableRegion[] | 'all' | undefined, disabledResizers: number | number[] | undefined, region1: ResizableRegion, region2: ResizableRegion): number | number[] | undefined => {

   if (!resizableRegions) {
      // Neither region is resizable
      return [0,1];
   }

   if (resizableRegions !== 'all') {
      if (resizableRegions.indexOf(region2) < 0) {
         if (resizableRegions.indexOf(region1) < 0) {
            // Neither region is resizable
            return [0,1];
         }
         else {
            // Only region1 is resizable
            if (disabledResizers===undefined || (Array.isArray(disabledResizers) && disabledResizers.length<1)) {
               return [1];
            }
            if (disabledResizers===0 || Array.isArray(disabledResizers) && disabledResizers.indexOf(0)>=0) {
               return [0,1];
            }
            return [1];
         }
      }
      else {
         if (resizableRegions.indexOf(region1) < 0) {
            // Only region2 is resizable
            if (disabledResizers===undefined || (Array.isArray(disabledResizers) && disabledResizers.length<1)) {
               return [0];
            }
            if (disabledResizers===1 || Array.isArray(disabledResizers) && disabledResizers.indexOf(1)>=0) {
               return [0,1];
            }
            return [0];
         }
      }
   }

   // Both regions are resizable
   return disabledResizers;
};

const getDefaultSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, defaultSizes: (number | string | 'layout')[] | undefined, region1: ResizableRegion, region2: ResizableRegion) => {
   return getSizeArray(resizableRegions,defaultSizes,region1,region2,'layout',1);
};

const getMinSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, minSizes: number[] | undefined, region1: ResizableRegion, region2: ResizableRegion) => {
   return getSizeArray(resizableRegions,minSizes,region1,region2,0,0);
};

const getMaxSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, maxSizes: number[] | undefined, region1: ResizableRegion, region2: ResizableRegion) => {
   return getSizeArray(resizableRegions,maxSizes,region1,region2,-1,-1);
};

const getDefaultHorizontalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, defaultHorizontalSizes: (number | string | 'layout')[] | undefined) => {
   return getDefaultSizes(resizableRegions, defaultHorizontalSizes, 'left', 'right');
};

const getDefaultVerticalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, defaultVerticalSizes: (number | string | 'layout')[] | undefined) => {
   return getDefaultSizes(resizableRegions, defaultVerticalSizes, 'top', 'bottom');
};

const getMinHorizontalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, minHorizontalSizes: number[] | undefined) => {
   return getMinSizes(resizableRegions, minHorizontalSizes, 'left', 'right');
};

const getMinVerticalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, minVerticalSizes: number[] | undefined) => {
   return getMinSizes(resizableRegions, minVerticalSizes, 'top', 'bottom');
};

const getMaxHorizontalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, maxHorizontalSizes: number[] | undefined) => {
   return getMaxSizes(resizableRegions, maxHorizontalSizes, 'left', 'right');
};

const getMaxVerticalSizes = (resizableRegions: ResizableRegion[] | 'all' | undefined, maxVerticalSizes: number[] | undefined) => {
   return getMaxSizes(resizableRegions, maxVerticalSizes, 'top', 'bottom');
};

const getDisabledHorizontalResizers = (resizableRegions: ResizableRegion[] | 'all' | undefined, disabledHorizontalResizers: number | number[] | undefined) => {
   return getDisabledResizers(resizableRegions, disabledHorizontalResizers, 'left', 'right');
};

const getDisabledVerticalResizers = (resizableRegions: ResizableRegion[] | 'all' | undefined, disabledVerticalResizers: number | number[] | undefined) => {
   return getDisabledResizers(resizableRegions, disabledVerticalResizers, 'top', 'bottom');
};
